import {FC, useEffect, useState} from 'react';
import {getUploadScan} from '@/rest/api';
import './UploadScanNotice.less';

/**
 * What the scan found when a document arrived through an upload Deep Link (OWA-1439). The server
 * scans a file on arrival and records the kinds of Protected Data it found, or that it could read
 * no text and so did not scan it. This shows that record on the document. It names kinds only;
 * no matched value is stored or shown. A document that arrived any other way shows nothing.
 *
 * The record describes the file as it arrived, never the document's current contents: a later
 * edit or version, a restored draft or an older version shown in the editor was not checked.
 */

export type UploadScan = {
    scanned: boolean;
    kinds: string[];
    receivedAt: string;
};

const KIND_NAMES: Record<string, string> = {
    cardNumber: 'Card numbers',
    ssn: 'Social Security numbers',
    bankAccount: 'Bank account numbers',
    routingNumber: 'Bank routing numbers',
    passport: 'Passport numbers',
    driversLicense: 'Driver\'s license numbers',
    taxId: 'Tax ID numbers',
    dateOfBirth: 'Dates of birth',
    phi: 'Health information',
};

/** The reader's name for a kind; a kind this client does not know is shown as the server names it. */
export function kindName(kind: string) {
    return KIND_NAMES[kind] ?? kind;
}

export const UploadScanSummary: FC<{scan: UploadScan}> = ({scan}) => {
    const received = new Date(scan.receivedAt).toLocaleString();
    let finding;
    if (!scan.scanned) {
        finding = <p>Outlaw could not read text in the file when it arrived, so it was not checked for card numbers or other protected information.</p>;
    } else if (scan.kinds.length === 0) {
        finding = <p>Outlaw checked the file when it arrived and found no card numbers or other protected information it looks for.</p>;
    } else {
        finding = <>
            <p>Outlaw checked the file when it arrived and found these kinds of protected information:</p>
            <ul aria-label='Protected information found'>
                {scan.kinds.map(kind => <li key={kind}>{kindName(kind)}</li>)}
            </ul>
        </>;
    }
    return <section className='upload-scan' aria-label='Upload check'>
        <p>This file arrived through an upload link on {received}.</p>
        {finding}
        <p className='upload-scan__later'>Changes made to this document after it arrived were not checked.</p>
    </section>;
};

const UploadScanNotice: FC<{documentId: string}> = ({documentId}) => {
    const [scan, setScan] = useState<UploadScan | null>(null);

    useEffect(() => {
        const controller = new AbortController();
        let active = true;
        setScan(null);
        getUploadScan(documentId, controller.signal).then(result => {
            if (active && result?.status === 200) setScan(result.body?.uploadScan ?? null);
        }).catch(err => {
            // Most documents have no record; a failed read shows nothing rather than an error on every document.
            if (active) console.warn('Could not read the upload check for a document', err);
        });
        return () => {
            active = false;
            controller.abort();
        };
    }, [documentId]);

    return scan ? <UploadScanSummary scan={scan}/> : null;
};

export default UploadScanNotice;
